import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Plus, Trash, PencilSimple, X } from '@phosphor-icons/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useProfessionalAvailability, useProfessionalMutations } from '@/hooks/useProfessionalsApi'
import type {
    AvailabilityException,
    AvailabilityExceptionInput,
    AvailabilityWindow,
    AvailabilityWindowInput,
} from '@/lib/api'

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

export interface EditableWindow {
    key: string
    start: string
    end: string
}

export interface DayState {
    weekday: number
    enabled: boolean
    windows: EditableWindow[]
}

let keySeed = 0
function nextKey(): string {
    keySeed += 1
    return `w-${keySeed}`
}

export function minutesToTime(minutes: number): string {
    const clamped = Math.max(0, Math.min(1439, minutes))
    const hours = Math.floor(clamped / 60)
    const mins = clamped % 60
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export function timeToMinutes(value: string): number | null {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
    if (!match) return null
    const hours = Number(match[1])
    const mins = Number(match[2])
    if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null
    return hours * 60 + mins
}

export function groupWindowsByDay(windows: AvailabilityWindow[]): DayState[] {
    return WEEKDAY_KEYS.map((_, weekday) => {
        const dayWindows = windows
            .filter((window) => window.weekday === weekday)
            .sort((a, b) => a.startMinutes - b.startMinutes)
        return {
            weekday,
            enabled: dayWindows.some((window) => window.active),
            windows: dayWindows.map((window) => ({
                key: nextKey(),
                start: minutesToTime(window.startMinutes),
                end: minutesToTime(window.endMinutes),
            })),
        }
    })
}

export function buildWindowsPayload(days: DayState[]): AvailabilityWindowInput[] {
    const payload: AvailabilityWindowInput[] = []
    for (const day of days) {
        if (!day.enabled) continue
        for (const window of day.windows) {
            const startMinutes = timeToMinutes(window.start)
            const endMinutes = timeToMinutes(window.end)
            if (startMinutes == null || endMinutes == null) continue
            if (endMinutes <= startMinutes) continue
            payload.push({
                weekday: day.weekday,
                startMinutes,
                endMinutes,
                active: true,
            })
        }
    }
    return payload
}

interface WeeklyHoursPanelProps {
    initialWindows: AvailabilityWindow[]
    isSaving: boolean
    onSave: (payload: AvailabilityWindowInput[]) => void
}

export function WeeklyHoursPanel({ initialWindows, isSaving, onSave }: WeeklyHoursPanelProps) {
    const { t } = useTranslation('team')
    const [days, setDays] = useState<DayState[]>(() => groupWindowsByDay(initialWindows))

    useEffect(() => {
        setDays(groupWindowsByDay(initialWindows))
    }, [initialWindows])

    const toggleDay = (weekday: number, enabled: boolean) => {
        setDays((prev) =>
            prev.map((day) => {
                if (day.weekday !== weekday) return day
                if (enabled && day.windows.length === 0) {
                    return { ...day, enabled, windows: [{ key: nextKey(), start: '09:00', end: '17:00' }] }
                }
                return { ...day, enabled }
            }),
        )
    }

    const addWindow = (weekday: number) => {
        setDays((prev) =>
            prev.map((day) =>
                day.weekday === weekday
                    ? { ...day, windows: [...day.windows, { key: nextKey(), start: '09:00', end: '17:00' }] }
                    : day,
            ),
        )
    }

    const removeWindow = (weekday: number, key: string) => {
        setDays((prev) =>
            prev.map((day) =>
                day.weekday === weekday
                    ? { ...day, windows: day.windows.filter((window) => window.key !== key) }
                    : day,
            ),
        )
    }

    const updateWindow = (weekday: number, key: string, patch: Partial<EditableWindow>) => {
        setDays((prev) =>
            prev.map((day) =>
                day.weekday === weekday
                    ? {
                          ...day,
                          windows: day.windows.map((window) =>
                              window.key === key ? { ...window, ...patch } : window,
                          ),
                      }
                    : day,
            ),
        )
    }

    return (
        <div className="space-y-4">
            <div className="divide-y rounded-md border">
                {days.map((day) => {
                    const label = t(`availability.weekdays.${WEEKDAY_KEYS[day.weekday]}`)
                    return (
                        <div key={day.weekday} className="space-y-3 p-4">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        aria-label={t('availability.enableDayAria', { day: label })}
                                        checked={day.enabled}
                                        onCheckedChange={(checked) => toggleDay(day.weekday, checked === true)}
                                    />
                                    <span className="font-medium">{label}</span>
                                </div>
                                {day.enabled && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => addWindow(day.weekday)}
                                    >
                                        <Plus className="mr-1 h-4 w-4" />
                                        {t('availability.addWindow')}
                                    </Button>
                                )}
                            </div>

                            {day.enabled &&
                                (day.windows.length === 0 ? (
                                    <p className="pl-11 text-sm text-muted-foreground">
                                        {t('availability.noWindows')}
                                    </p>
                                ) : (
                                    <div className="space-y-2 pl-11">
                                        {day.windows.map((window) => (
                                            <div key={window.key} className="flex items-center gap-2">
                                                <Input
                                                    type="time"
                                                    className="max-w-32"
                                                    aria-label={t('availability.startAria', { day: label })}
                                                    value={window.start}
                                                    onChange={(e) =>
                                                        updateWindow(day.weekday, window.key, {
                                                            start: e.target.value,
                                                        })
                                                    }
                                                />
                                                <span className="text-muted-foreground">–</span>
                                                <Input
                                                    type="time"
                                                    className="max-w-32"
                                                    aria-label={t('availability.endAria', { day: label })}
                                                    value={window.end}
                                                    onChange={(e) =>
                                                        updateWindow(day.weekday, window.key, {
                                                            end: e.target.value,
                                                        })
                                                    }
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    aria-label={t('availability.removeWindow')}
                                                    onClick={() => removeWindow(day.weekday, window.key)}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                        </div>
                    )
                })}
            </div>

            <div className="flex justify-end">
                <Button disabled={isSaving} onClick={() => onSave(buildWindowsPayload(days))}>
                    {isSaving ? t('availability.saving') : t('availability.saveHours')}
                </Button>
            </div>
        </div>
    )
}

interface ExceptionDraft {
    date: string
    isAllDay: boolean
    start: string
    end: string
    reason: string
}

function toDraft(exception: AvailabilityException | null): ExceptionDraft {
    if (!exception) {
        return { date: '', isAllDay: true, start: '09:00', end: '17:00', reason: '' }
    }
    return {
        date: exception.date,
        isAllDay: exception.isAllDay,
        start: exception.startMinutes != null ? minutesToTime(exception.startMinutes) : '09:00',
        end: exception.endMinutes != null ? minutesToTime(exception.endMinutes) : '17:00',
        reason: exception.reason ?? '',
    }
}

export function buildExceptionPayload(draft: ExceptionDraft): AvailabilityExceptionInput | null {
    if (!draft.date) return null
    const base: AvailabilityExceptionInput = {
        date: draft.date,
        isAllDay: draft.isAllDay,
        reason: draft.reason.trim() || null,
    }
    if (draft.isAllDay) {
        return { ...base, startMinutes: null, endMinutes: null }
    }
    const startMinutes = timeToMinutes(draft.start)
    const endMinutes = timeToMinutes(draft.end)
    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) return null
    return { ...base, startMinutes, endMinutes }
}

interface ExceptionDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    exception: AvailabilityException | null
    isSaving: boolean
    onSubmit: (payload: AvailabilityExceptionInput) => void
}

function ExceptionDialog({ open, onOpenChange, exception, isSaving, onSubmit }: ExceptionDialogProps) {
    const { t } = useTranslation('team')
    const [draft, setDraft] = useState<ExceptionDraft>(() => toDraft(exception))

    useEffect(() => {
        if (open) setDraft(toDraft(exception))
    }, [open, exception])

    const payload = buildExceptionPayload(draft)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {exception
                            ? t('availability.exceptions.editTitle')
                            : t('availability.exceptions.addTitle')}
                    </DialogTitle>
                    <DialogDescription>{t('availability.exceptions.dialogDescription')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="exception-date">{t('availability.exceptions.dateLabel')}</Label>
                        <Input
                            id="exception-date"
                            type="date"
                            value={draft.date}
                            onChange={(e) => setDraft((prev) => ({ ...prev, date: e.target.value }))}
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <Switch
                            id="exception-all-day"
                            aria-label={t('availability.exceptions.allDay')}
                            checked={draft.isAllDay}
                            onCheckedChange={(checked) =>
                                setDraft((prev) => ({ ...prev, isAllDay: checked === true }))
                            }
                        />
                        <Label htmlFor="exception-all-day">{t('availability.exceptions.allDay')}</Label>
                    </div>

                    {!draft.isAllDay && (
                        <div className="flex items-center gap-2">
                            <Input
                                type="time"
                                className="max-w-32"
                                aria-label={t('availability.exceptions.startLabel')}
                                value={draft.start}
                                onChange={(e) => setDraft((prev) => ({ ...prev, start: e.target.value }))}
                            />
                            <span className="text-muted-foreground">–</span>
                            <Input
                                type="time"
                                className="max-w-32"
                                aria-label={t('availability.exceptions.endLabel')}
                                value={draft.end}
                                onChange={(e) => setDraft((prev) => ({ ...prev, end: e.target.value }))}
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="exception-reason">{t('availability.exceptions.reasonLabel')}</Label>
                        <Textarea
                            id="exception-reason"
                            rows={2}
                            value={draft.reason}
                            onChange={(e) => setDraft((prev) => ({ ...prev, reason: e.target.value }))}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('availability.exceptions.cancel')}
                    </Button>
                    <Button
                        disabled={isSaving || !payload}
                        onClick={() => payload && onSubmit(payload)}
                    >
                        {t('availability.exceptions.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function formatExceptionTime(exception: AvailabilityException): string | null {
    if (exception.isAllDay || exception.startMinutes == null || exception.endMinutes == null) {
        return null
    }
    return `${minutesToTime(exception.startMinutes)} – ${minutesToTime(exception.endMinutes)}`
}

interface AvailabilityEditorProps {
    userId: string
    professionalName: string
    onClose?: () => void
}

export function AvailabilityEditor({ userId, professionalName, onClose }: AvailabilityEditorProps) {
    const { t } = useTranslation('team')
    const { data, isLoading, refetch } = useProfessionalAvailability(userId)
    const mutations = useProfessionalMutations()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingException, setEditingException] = useState<AvailabilityException | null>(null)

    const windows = useMemo(() => data?.windows ?? [], [data])
    const exceptions = useMemo(
        () =>
            [...(data?.exceptions ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
        [data],
    )

    const handleSaveWindows = async (payload: AvailabilityWindowInput[]) => {
        try {
            await mutations.setAvailabilityWindows(userId, payload)
            toast.success(t('availability.toasts.hoursSaved'))
            refetch()
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : t('availability.toasts.error'))
        }
    }

    const handleSubmitException = async (payload: AvailabilityExceptionInput) => {
        try {
            if (editingException) {
                await mutations.updateAvailabilityException(userId, editingException.id, payload)
            } else {
                await mutations.addAvailabilityException(userId, payload)
            }
            toast.success(t('availability.toasts.exceptionSaved'))
            setDialogOpen(false)
            setEditingException(null)
            refetch()
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : t('availability.toasts.error'))
        }
    }

    const handleDeleteException = async (exceptionId: string) => {
        try {
            await mutations.deleteAvailabilityException(userId, exceptionId)
            toast.success(t('availability.toasts.exceptionDeleted'))
            refetch()
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : t('availability.toasts.error'))
        }
    }

    return (
        <div className="space-y-6" data-testid="availability-editor">
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base">{t('availability.title')}</CardTitle>
                        <CardDescription>
                            {t('availability.subtitle', { name: professionalName })}
                        </CardDescription>
                    </div>
                    {onClose && (
                        <Button variant="ghost" size="sm" onClick={onClose}>
                            {t('availability.close')}
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="h-40 w-full animate-pulse rounded-lg bg-muted" />
                    ) : (
                        <WeeklyHoursPanel
                            initialWindows={windows}
                            isSaving={mutations.isLoading}
                            onSave={handleSaveWindows}
                        />
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-3">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base">{t('availability.exceptions.title')}</CardTitle>
                        <CardDescription>{t('availability.exceptions.subtitle')}</CardDescription>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditingException(null)
                            setDialogOpen(true)
                        }}
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        {t('availability.exceptions.add')}
                    </Button>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="h-16 w-full animate-pulse rounded-lg bg-muted" />
                    ) : exceptions.length === 0 ? (
                        <p className="py-4 text-sm text-muted-foreground">
                            {t('availability.exceptions.empty')}
                        </p>
                    ) : (
                        <ul className="divide-y rounded-md border">
                            {exceptions.map((exception) => {
                                const timeLabel = formatExceptionTime(exception)
                                return (
                                    <li
                                        key={exception.id}
                                        className="flex items-center justify-between gap-3 p-3"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium">{exception.date}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {exception.isAllDay
                                                    ? t('availability.exceptions.allDay')
                                                    : timeLabel}
                                                {exception.reason ? ` · ${exception.reason}` : ''}
                                            </div>
                                        </div>
                                        <div className="flex shrink-0 gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={t('availability.exceptions.edit')}
                                                onClick={() => {
                                                    setEditingException(exception)
                                                    setDialogOpen(true)
                                                }}
                                            >
                                                <PencilSimple className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={t('availability.exceptions.delete')}
                                                disabled={mutations.isLoading}
                                                onClick={() => handleDeleteException(exception.id)}
                                            >
                                                <Trash className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <ExceptionDialog
                open={dialogOpen}
                onOpenChange={(open) => {
                    setDialogOpen(open)
                    if (!open) setEditingException(null)
                }}
                exception={editingException}
                isSaving={mutations.isLoading}
                onSubmit={handleSubmitException}
            />
        </div>
    )
}
